/**
 * A scene's light (lifted from lantern-water's `main.js` `updateLighting`,
 * its lights, and `window-lighting.js`): the sun (a directional key light
 * with a cached shadow map), the fill, the hemisphere sky, and the scene's
 * point and spot lights (the house lanterns and two quay lamps, the stair
 * court's sun patch; the hearth, the chandelier and the two window lights
 * with their leaded-glass gobo), each blended from day to night and gently
 * flickering (the record's waves: one for the lanterns, two for the hearth
 * and the chandelier; none under reduced motion). Medium is the stress test as it was: every light, the shadowed
 * ones casting into their maps. Off keeps the sun and the sky only, with no
 * shadow map (Rendering page: no dynamic lights beyond the directional key
 * and the hemisphere at Off).
 */
import type { Scene } from '@ab/content-schema';
import * as THREE from 'three';
import { sceneLook } from './constants.ts';
import type { Look } from './look.ts';

const three = (p: { readonly x: number; readonly y: number; readonly h: number }, out = new THREE.Vector3()): THREE.Vector3 => out.set(p.x, p.h, -p.y);

/** The apothecary's leaded window as a spot light's gobo (`window-lighting.js`): RGB modulates the light; black outside the arch. */
const leadedWindow = (): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (ctx !== null) {
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, 256, 256);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(61, 222);
    ctx.lineTo(61, 104);
    ctx.arc(128, 104, 67, Math.PI, Math.PI * 2);
    ctx.lineTo(195, 222);
    ctx.closePath();
    ctx.clip();
    ctx.fillStyle = '#fff4df';
    ctx.fillRect(0, 0, 256, 256);
    ctx.strokeStyle = '#13120f';
    ctx.lineWidth = 3;
    for (let i = -256; i < 512; i += 44) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + 175, 256);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i - 175, 256);
      ctx.stroke();
    }
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(128, 20);
    ctx.lineTo(128, 240);
    ctx.moveTo(50, 137);
    ctx.lineTo(205, 137);
    ctx.stroke();
    ctx.restore();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

export type LightCounts = { readonly point: number; readonly spot: number; readonly shadowed: number };

export type SceneLights = {
  readonly group: THREE.Group;
  readonly sun: THREE.DirectionalLight;
  setTier(medium: boolean): void;
  /**
   * The time of day's look (ADR 0033: the sun, the fill, the sky, and each light's day-to-night blend by the look's
   * `lamps`), the flicker's time, the motion strength (0 under reduced motion: no flicker), and a factor on the sun
   * (the airship's passing clouds). A look with a sun direction moves the sun to stand that way from its target.
   */
  update(look: Look, time: number, motion: number, sunScale: number): void;
  counts(): LightCounts;
  /** Frees the shadow maps' GPU copies (Off). */
  release(): void;
  dispose(): void;
};

/** The sun's shadow camera and biases: the town's (`sceneLook.sun`) or, given, another's (the airship's `shipLook.sun`). */
export type SunShadow = { readonly near: number; readonly far: number; readonly bias: number; readonly normalBias: number; readonly distance: number };

const s = sceneLook.sun;

export const createSceneLights = (scene: Scene, shadow: SunShadow = { near: s.near, far: s.far, bias: s.bias, normalBias: s.normalBias, distance: 0 }): SceneLights => {
  const l = scene.lighting;
  const group = new THREE.Group();
  group.name = 'scene-lights';
  const sun = new THREE.DirectionalLight('#ffffff', 1);
  three(l.sun.position, sun.position);
  const sunTarget = l.sun.target === undefined ? new THREE.Vector3() : three(l.sun.target);
  sun.target.position.copy(sunTarget);
  sun.castShadow = true;
  sun.shadow.mapSize.set(l.sun.shadowMapSize, l.sun.shadowMapSize);
  const depth = l.sun.depth ?? l.sun.extent * s.aspect;
  Object.assign(sun.shadow.camera, { left: -l.sun.extent, right: l.sun.extent, top: depth, bottom: -depth, near: shadow.near, far: shadow.far });
  sun.shadow.camera.updateProjectionMatrix();
  sun.shadow.normalBias = shadow.normalBias;
  sun.shadow.bias = shadow.bias;
  // The fill exists only in a scene whose presets light it (the town's): an unlit one would still cost every lit
  // shader a second directional light (three counts a visible light whatever its intensity).
  const lit = l.fill !== undefined && Object.values(l.presets).some((p) => (p.fill ?? 0) > 0);
  const fill = lit && l.fill !== undefined ? new THREE.DirectionalLight(l.fill.color, 0) : null;
  if (fill !== null && l.fill !== undefined) three(l.fill.position, fill.position);
  const hemi = new THREE.HemisphereLight('#ffffff', '#000000', 1);
  group.add(sun, sun.target, hemi);
  if (fill !== null) group.add(fill);
  const gobo = scene.lights.some((x) => x.gobo !== undefined) ? leadedWindow() : null;
  const dynamic = scene.lights.map((spec) => {
    const light =
      spec.kind === 'point'
        ? new THREE.PointLight(spec.color, spec.intensity, spec.distance, spec.decay)
        : new THREE.SpotLight(spec.color, spec.intensity, spec.distance, spec.angle ?? Math.PI / 3, spec.penumbra ?? 0, spec.decay);
    three(spec, light.position);
    if (light instanceof THREE.SpotLight) {
      if (spec.target !== undefined) three(spec.target, light.target.position);
      if (spec.gobo !== undefined && gobo !== null) light.map = gobo;
      group.add(light.target);
    }
    if (spec.shadow) {
      const size = spec.shadowMapSize ?? 1024;
      light.shadow.mapSize.set(size, size);
      light.shadow.bias = s.bias;
      light.shadow.normalBias = spec.kind === 'spot' && spec.gobo !== undefined ? s.goboNormalBias : s.normalBias;
      if (light instanceof THREE.SpotLight && spec.gobo !== undefined) {
        light.shadow.camera.near = s.goboNear;
        light.shadow.camera.far = spec.distance;
      }
    }
    light.name = spec.id;
    group.add(light);
    return { spec, light };
  });
  let medium = false;
  const apply = (): void => {
    if (fill !== null) fill.visible = medium;
    sun.castShadow = medium;
    for (const { spec, light } of dynamic) {
      light.visible = medium;
      light.castShadow = medium && spec.shadow;
    }
  };
  apply();
  return {
    group,
    sun,
    setTier(next) {
      medium = next;
      apply();
    },
    update(look, time, motion, sunScale) {
      const n = look.lamps;
      sun.color.copy(look.sunColor);
      sun.intensity = look.sunIntensity * sunScale;
      if (look.sunDirection.lengthSq() > 0) sun.position.copy(sunTarget).addScaledVector(look.sunDirection, shadow.distance);
      if (fill !== null) fill.intensity = look.fill;
      hemi.color.copy(look.hemiSky);
      hemi.groundColor.copy(look.hemiGround);
      hemi.intensity = look.hemiIntensity;
      for (const { spec, light } of dynamic) {
        // `main.js` `updateLighting`: 1 + Σ sin(time × speed + phase) × amount, scaled by the motion strength.
        let wave = 0;
        for (const w of spec.flicker ?? []) wave += Math.sin(time * w.speed + w.phase) * w.amount;
        light.intensity = THREE.MathUtils.lerp(spec.intensity, spec.nightIntensity, n) * (1 + wave * motion);
      }
    },
    counts() {
      let point = 0;
      let spot = 0;
      let shadowed = sun.castShadow ? 1 : 0;
      for (const { light } of dynamic) {
        if (!light.visible) continue;
        if (light instanceof THREE.PointLight) point += 1;
        else spot += 1;
        if (light.castShadow) shadowed += 1;
      }
      return { point, spot, shadowed };
    },
    release() {
      for (const light of [sun, ...dynamic.map((d) => d.light)]) {
        light.shadow.map?.dispose();
        light.shadow.map = null;
      }
      gobo?.dispose();
    },
    dispose() {
      group.removeFromParent();
      for (const light of [sun, hemi, ...(fill === null ? [] : [fill]), ...dynamic.map((d) => d.light)]) light.dispose();
      gobo?.dispose();
    },
  };
};
